/**
 * API Endpoints
 *
 * Centralized endpoint constants to avoid magic strings throughout the codebase.
 */

export const ENDPOINTS = {
  // Authentication
  AUTH_LOGIN: "/api/v1/auth/login",
  AUTH_REFRESH: "/api/v1/auth/refresh",
  AUTH_LOGOUT: "/api/v1/auth/logout",
  AUTH_ME: "/api/v1/auth/me",
  // SSO endpoints (per-org SSO via Keycloak — backend Tasks 12-14)
  AUTH_ORG_CONFIG: "/api/v1/auth/org-config",
  AUTH_SSO_INIT: "/api/v1/auth/sso/init",
  AUTH_SSO_CALLBACK: "/api/v1/auth/sso/callback",
  AUTH_MFA_CHECK: "/api/v1/auth/mfa/check",
  AUTH_MFA_STATUS: "/api/v1/auth/mfa/status",
  AUTH_MFA_SETUP: "/api/v1/auth/mfa/setup",
  AUTH_MFA_DISABLE: "/api/v1/auth/mfa/disable",
  AUTH_FORGOT_PASSWORD: "/api/v1/auth/forgot-password",
  AUTH_CHANGE_PASSWORD: "/api/v1/auth/change-password",
  AUTH_UPDATE_PROFILE: "/api/v1/auth/profile",

  // Admin (User Management)
  ADMIN_USERS: "/api/v1/admin/users",
  ADMIN_USERS_INVITE: "/api/v1/admin/users/invite",
  ADMIN_USERS_RESEND_INVITE: (id: string) =>
    `/api/v1/admin/users/${id}/resend-invite`,
  ADMIN_USERS_ROLE: (id: string) => `/api/v1/admin/users/${id}/role`,
  ADMIN_USERS_DISABLE: (id: string) => `/api/v1/admin/users/${id}/disable`,
  ADMIN_USERS_ENABLE: (id: string) => `/api/v1/admin/users/${id}/enable`,
  ADMIN_USERS_DELETE: (id: string) => `/api/v1/admin/users/${id}`,

  // Applications
  STORM_APPLICATIONS: "/api/v1/storm-applications",

  GEO_HIERARCHIES: "/api/v1/geographic/hierarchies",
  GEO_NODES: "/api/v1/geographic/nodes",

  // Organizations
  ORGANIZATION_CURRENT: "/api/v1/organization",
  ORGANIZATIONS: "/organizations",
  ORGANIZATION: (id: string) => `/organizations/${id}`,

  // Storm Impact Models
  STORM_IMPACT_MODELS: "/api/v1/storm-impact-models",
  STORM_IMPACT_MODEL: (id: string) => `/storm-impact-models/${id}`,

  // Weather Models
  WEATHER_MODELS: "/api/v1/weather-models",
  WEATHER_MODEL: (id: string) => `/api/v1/weather-models/${id}`,

  // Geographic Hierarchies
  GEOGRAPHIC_HIERARCHIES: "/geographic-hierarchies",
  GEOGRAPHIC_HIERARCHY: (id: string) => `/geographic-hierarchies/${id}`,
  GEOGRAPHIC_NODES: "/geographic-nodes",
  GEOGRAPHIC_NODE: (id: string) => `/geographic-nodes/${id}`,

  // Daily Forecasts
  DAILY_FORECASTS: "/daily-forecasts",
  DAILY_FORECAST: (id: string) => `/daily-forecasts/${id}`,

  // Available Runs
  AVAILABLE_RUNS: "/api/v1/forecasts/available-runs",

  // Forecasts (main forecast data endpoint)
  FORECASTS: "/api/v1/forecasts",

  // Forecast Variables
  FORECAST_VARIABLES: "/forecast-variables",
  FORECAST_VARIABLE: (id: string) => `/forecast-variables/${id}`,

  // GeoJSON
  GEOJSON: (organizationId: string) =>
    `/organizations/${organizationId}/geojson`,

  // Notification Settings (config management)
  NOTIFICATION_SETTINGS: "/api/v1/notification-settings",
  NOTIFICATION_SETTINGS_DISCOVERY: "/api/v1/notification-settings/discovery",
  NOTIFICATION_SETTING: (id: string) => `/api/v1/notification-settings/${id}`,

  // Notifications Inbox
  NOTIFICATIONS: "/api/v1/notifications",
  NOTIFICATIONS_STATS: "/api/v1/notifications/stats",
  NOTIFICATION_BY_DATE: (date: string) => `/api/v1/notifications/${date}`,
  NOTIFICATIONS_READ_ALL: "/api/v1/notifications/read-all",

  // API Keys
  API_KEY: "/api/v1/api-key",

  // Documents
  DOCUMENTS: "/api/v1/documents",
  DOCUMENT_ACCESS: (id: string) => `/api/v1/documents/${id}/access`,

  // Storms (Hurricane Impact Predictions)
  STORMS: "/api/v1/storms",
  STORM_METADATA: (id: string) => `/api/v1/storms/${id}/metadata`,
  STORM_SCENARIO: (id: string) => `/api/v1/storms/${id}/scenario`,
  STORM_SNAPSHOT_TRACK: (snapshotId: string) =>
    `/api/v1/storms/snapshots/${snapshotId}/track`,
  STORM_SNAPSHOT_IMPACTS: (snapshotId: string) =>
    `/api/v1/storms/snapshots/${snapshotId}/impacts`,

  // User Configs (per-user persistent settings)
  USER_CONFIGS: "/api/v1/user-configs",
  USER_CONFIG: (key: string) => `/api/v1/user-configs/${key}`,

  // Exports
  EXPORT_FORECASTS: "/api/v1/exports/forecasts",

  // Generic scenario APIs (WIP district/region and future storm modules)
  SCENARIO_PARAMETERS: (stormId: string) =>
    `/api/v1/storms/${stormId}/scenarios/parameters`,
  SCENARIO_MATCH: (stormId: string) =>
    `/api/v1/storms/${stormId}/scenarios/match`,
  SCENARIO_IMPACTS: (scenarioMetadataId: string) =>
    `/api/v1/storms/scenarios/${scenarioMetadataId}/impacts`,
  REGION_IMPACTS: (stormId: string) =>
    `/api/v1/storms/${stormId}/region-impacts`,
} as const;
